import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AssinaturaCancelada,
  AssinaturaCanceladaPendente,
  BloqueioAdesao,
  CartaoCriptografado,
  ClienteAdesao,
  ComparativoAdesao,
  ElegibilidadeAdesao,
  ElegibilidadeMigracao,
  FamiliaAdesao,
  LinkCheckout,
  OrigemOferta,
  PagamentoAdesao,
  PlacarVendaCartao,
  PendenciaCartaoSim,
  RegularizacaoGerada,
  ResultadoAdesao,
  ResultadoMigracao,
  SituacaoVendaCartao,
  StatusAssinante,
  TermoAdesao,
  ValorVisita,
  VencimentoRegularizacao,
} from '../../interfaces/adesao.dto';
import { mensagemDoBlob, nomeDoArquivo, salvarBlob } from '../../utils/baixar-arquivo.util';

/**
 * Oferta de plano CartãoSiM no step 3. O front nunca fala com o CartãoSiM: tudo passa
 * pelo back da Fila, que identifica o vendedor pelo token de login.
 */
@Injectable({ providedIn: 'root' })
export class AdesaoService {
  private readonly apiURL = `${environment.apiUrl}/v${environment.apiVersion}/adesao`;

  constructor(private http: HttpClient) {}

  /** Falha vira "não elegível": a oferta some e o atendimento segue. */
  async elegibilidade(
    clinicId: number, personId: number, convenioParticular: boolean, cpf: string,
  ): Promise<ElegibilidadeAdesao> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(
        `${this.apiURL}/elegibilidade?clinicId=${clinicId}&personId=${personId}` +
        `&convenioParticular=${convenioParticular}&cpf=${encodeURIComponent(cpf ?? '')}`,
      ));
      return resposta?.data?.[0] ?? { pode: false, motivo: 'erro' };
    } catch {
      return { pode: false, motivo: 'erro' };
    }
  }

  /** Famílias na ordem da oferta. Falha vira lista vazia: a oferta some. */
  async vitrine(clinicId: number): Promise<FamiliaAdesao[]> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(`${this.apiURL}/vitrine?clinicId=${clinicId}`));
      return resposta?.data ?? [];
    } catch {
      return [];
    }
  }

  /** Falha vira lista vazia: a tela só não mostra o com/sem plano. */
  async comparativo(
    clinicId: number, itens: { cdProcedimento: number; quantidade: number }[],
  ): Promise<ComparativoAdesao[]> {
    try {
      const resposta = await firstValueFrom(this.http.post<any>(`${this.apiURL}/comparativo`, { clinicId, itens }));
      return resposta?.data ?? [];
    } catch {
      return [];
    }
  }

  /** Visita no plano da assinatura em atraso. Falha ou sem pendência vira null. */
  async comparativoPendencia(
    clinicId: number, personId: number, itens: { cdProcedimento: number; quantidade: number }[],
  ): Promise<ComparativoAdesao | null> {
    try {
      const resposta = await firstValueFrom(this.http.post<any>(`${this.apiURL}/pendencia/comparativo`, { clinicId, personId, itens }));
      return resposta?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  /** Convênio/categoria que o TASY dá ao paciente (o que a trigger grava na conta). */
  async planoPaciente(personId: number): Promise<{ cdConvenio: number; cdCategoria: number } | null> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(`${this.apiURL}/plano-paciente?personId=${personId}`));
      return resposta?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  /** Valor da visita no convênio efetivo da conta. Falha vira null: o resumo só não mostra o valor. */
  async valorVisita(
    clinicId: number, personId: number, cdConvenio: number | null, cdCategoria: number | null,
    itens: { cdProcedimento: number; quantidade: number }[],
  ): Promise<ValorVisita | null> {
    try {
      const resposta = await firstValueFrom(this.http.post<any>(`${this.apiURL}/valor-visita`,
        { clinicId, personId, cdConvenio, cdCategoria, itens }));
      return resposta?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  async cliente(cpf: string): Promise<ClienteAdesao | null> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(`${this.apiURL}/cliente/${encodeURIComponent(cpf)}`));
      return resposta?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  /** Erro sobe com a mensagem real da API para o operador ver o que fazer. */
  async criarAssinatura(payload: {
    cpf: string; personId: number; planoId: number; paymentMethodId: number;
    paymentMethodCode: string; establishmentId: number; diaVencimento: number | null;
    origem?: OrigemOferta;
  }): Promise<ResultadoAdesao> {
    const resposta = await this.enviar(this.http.post<any>(`${this.apiURL}/assinatura`, payload));
    const dado = resposta?.data?.[0];

    if (!dado) throw new Error(this.mensagem(resposta));
    return dado;
  }

  /** Assinatura já cobrada no cartão digitado; a ativação chega pelo laço, como na maquininha. */
  async assinaturaCartao(payload: {
    cpf: string; personId: number; planoId: number; paymentMethodId: number; establishmentId: number;
    diaVencimento?: number | null; parcelas: number; origem?: OrigemOferta;
    cartao: CartaoCriptografado & { holderName: string; brand: string; checkoutAttemptId: string };
  }): Promise<ResultadoAdesao> {
    const resposta = await this.enviar(this.http.post<any>(`${this.apiURL}/assinatura/cartao`, payload));
    const dado = resposta?.data?.[0];

    if (!dado) throw new Error(this.mensagem(resposta));
    return dado;
  }

  /** Falha vira "erro": a faixa de migração some e o atendimento segue. */
  async elegibilidadeMigracao(clinicId: number, personId: number): Promise<ElegibilidadeMigracao> {
    const vazio: ElegibilidadeMigracao = { pode: false, motivo: 'erro', atual: null, destinos: [], dependentes: [], pendente: null };
    try {
      const resposta = await firstValueFrom(this.http.get<any>(
        `${this.apiURL}/migracao/elegibilidade?clinicId=${clinicId}&personId=${personId}`));
      return resposta?.data?.[0] ?? vazio;
    } catch {
      return vazio;
    }
  }

  async criarMigracao(payload: {
    cpf: string; personId: number; planoId: number; paymentMethodId: number; paymentMethodCode: string;
    establishmentId: number; diaVencimento: number | null; dependentesMantidos: number[]; origem?: OrigemOferta;
    parcelas?: number; cartao?: CartaoCriptografado & { holderName: string; brand: string; checkoutAttemptId: string };
  }): Promise<ResultadoAdesao> {
    const resposta = await this.enviar(this.http.post<any>(`${this.apiURL}/migracao`, payload));
    const dado = resposta?.data?.[0];

    if (!dado) throw new Error(this.mensagem(resposta));
    return dado;
  }

  async resultadoMigracao(subscriptionId: number, personId: number, antiga: number, mantidos: number[]): Promise<ResultadoMigracao | null> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(
        `${this.apiURL}/migracao/${subscriptionId}/resultado?personId=${personId}&antiga=${antiga}&mantidos=${mantidos.join(',')}`));
      return resposta?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  async linkPagamento(subscriptionId: number): Promise<PagamentoAdesao> {
    const resposta = await this.enviar(this.http.get<any>(`${this.apiURL}/assinatura/${subscriptionId}/pagamento`));
    const dado = resposta?.data?.[0];

    if (!dado?.url) throw new Error(this.mensagem(resposta));
    return dado;
  }

  /** Devolve o que saiu por canal; erro só quando nenhum canal pedido saiu. */
  async enviarLink(
    subscriptionId: number, clientId: number, url: string, clinicId: number, email: boolean, whatsApp: boolean,
  ): Promise<PagamentoAdesao> {
    const resposta = await this.enviar(this.http.post<any>(
      `${this.apiURL}/assinatura/${subscriptionId}/link`,
      { clientId, url, clinicId, email, whatsApp },
    ));
    const dado = resposta?.data?.[0];

    if (!dado?.enviado) throw new Error(this.mensagem(resposta));
    return dado;
  }

  /** Falha de consulta vira "não liberado": a venda espera o envio do termo. */
  async termo(cpf: string): Promise<TermoAdesao> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(`${this.apiURL}/termo/${encodeURIComponent(cpf)}`));
      return resposta?.data?.[0] ?? { assinado: false, assinadoEm: null, enviadoEm: null, liberado: false };
    } catch {
      return { assinado: false, assinadoEm: null, enviadoEm: null, liberado: false };
    }
  }

  async enviarTermo(payload: {
    cpf: string; planoId: number; planoNome: string; paymentMethodCode: string; clinicId: number;
    canal: 'email' | 'sms'; destino: string;
  }): Promise<TermoAdesao> {
    const resposta = await this.enviar(this.http.post<any>(`${this.apiURL}/termo`, payload));
    const dado = resposta?.data?.[0];

    if (!dado) throw new Error(this.mensagem(resposta));
    return dado;
  }

  /** PDF do termo assinado em papel: libera a venda daquele plano por 24h. */
  async importarTermo(cpf: string, planoId: number, clinicId: number, arquivo: File): Promise<TermoAdesao> {
    const form = new FormData();
    form.append('cpf', cpf);
    form.append('planoId', String(planoId));
    form.append('clinicId', String(clinicId));
    form.append('arquivo', arquivo, arquivo.name);
    const resposta = await this.enviar(this.http.post<any>(`${this.apiURL}/termo/importar`, form));
    const dado = resposta?.data?.[0];

    if (!dado) throw new Error(this.mensagem(resposta));
    return dado;
  }

  /** Termo padrão do plano em PDF, para imprimir e preencher no papel. */
  async baixarTermoModelo(planoId: number, clinicId: number): Promise<void> {
    try {
      const resposta = await firstValueFrom(this.http.get(`${this.apiURL}/termo/modelo`, {
        params: new HttpParams().set('planoId', planoId).set('clinicId', clinicId),
        responseType: 'blob',
        observe: 'response',
      }));
      salvarBlob(resposta.body as Blob, nomeDoArquivo(resposta.headers.get('Content-Disposition'), 'termo-adesao.pdf'));
    } catch (e: any) {
      throw new Error(await mensagemDoBlob(e, 'Não foi possível baixar o termo agora.'));
    }
  }

  /** Link do checkout (cartão de crédito); com canal, o back também envia ao paciente. */
  async checkoutLink(body: {
    cpf: string; clinicId: number; planoId: number; diaVencimento?: number | null;
    canal?: 'email' | 'whatsapp' | null; destino?: string;
  }): Promise<LinkCheckout> {
    const resposta = await this.enviar(this.http.post<any>(`${this.apiURL}/checkout-link`, body));
    const dado = resposta?.data?.[0];

    if (!dado?.link) throw new Error(this.mensagem(resposta));
    return dado;
  }

  /** Valor que vai para a maquineta; null quando a assinatura ainda não tem cobrança. */
  async cobranca(subscriptionId: number): Promise<PagamentoAdesao | null> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(`${this.apiURL}/assinatura/${subscriptionId}/cobranca`));
      return resposta?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  /** Evento para o relatório de conversão. Nunca atrapalha a tela: falha é ignorada. */
  registrarEvento(
    tipo: string, familia: string, personId: number, clinicId: number, senhaId?: number, origem: OrigemOferta = null,
  ): void {
    this.http.post(`${this.apiURL}/evento`, { tipo, familia, personId, clinicId, nrSeqSenha: senhaId || null, origem }).subscribe({ error: () => undefined });
  }

  /** Abordagem da Venda Cartão: sem família, o tipo (adesão ou upsell) vai no detalhe. */
  registrarAbordagem(clinicId: number, personId: number, tipo: 'adesao' | 'upsell'): void {
    this.http.post(`${this.apiURL}/evento`, {
      tipo: 'venda_cartao_abordagem', familia: '', personId, clinicId, nrSeqSenha: null, origem: 'venda_cartao', detalhe: tipo,
    }).subscribe({ error: () => undefined });
  }

  /** Falha vira null: a tela some com a oferta e o atendimento segue. */
  async situacaoVendaCartao(clinicId: number, personId: number, cpf: string): Promise<SituacaoVendaCartao | null> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(
        `${this.apiURL}/venda-cartao/situacao?clinicId=${clinicId}&personId=${personId}&cpf=${encodeURIComponent(cpf ?? '')}`));
      return resposta?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  /** Qualquer erro, inclusive a trava do piloto, vira "indisponível". */
  async placarVendaCartao(clinicId: number): Promise<PlacarVendaCartao> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(`${this.apiURL}/venda-cartao/placar?clinicId=${clinicId}`));
      return resposta?.data?.[0] ?? { disponivel: false, abordagens: 0, recusas: 0, vendas: 0 };
    } catch {
      return { disponivel: false, abordagens: 0, recusas: 0, vendas: 0 };
    }
  }

  /** Cobra a 1ª parcela na maquineta pelo CartãoSiM; a ativação chega pelo laço. */
  async cobrarNoTerminal(subscriptionId: number, poiid: string, parcelas: number, clinicId: number): Promise<void> {
    const resposta = await this.enviar(this.http.post<any>(
      `${this.apiURL}/assinatura/${subscriptionId}/terminal`, { poiid, parcelas, clinicId },
    ));
    if (!resposta?.data?.[0]?.enviado) throw new Error(this.mensagem(resposta));
  }

  /** Única chamada do laço de ativação; é leitura local no Oracle, não bate no CartãoSiM. */
  async ativacao(personId: number): Promise<StatusAssinante> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(`${this.apiURL}/ativacao?personId=${personId}`));
      return resposta?.data?.[0] ?? { situacao: 'sem-assinatura', cdConvenio: null, cdCategoria: null };
    } catch {
      return { situacao: 'sem-assinatura', cdConvenio: null, cdCategoria: null };
    }
  }

  async pendencia(personId: number, subscriptionId?: number): Promise<PendenciaCartaoSim | null> {
    const assinatura = subscriptionId != null ? `&subscriptionId=${subscriptionId}` : '';
    try {
      const r = await firstValueFrom(this.http.get<any>(`${this.apiURL}/pendencia?personId=${personId}${assinatura}`));
      return r?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  async pendenciaCancelada(personId: number): Promise<AssinaturaCanceladaPendente[]> {
    try {
      const r = await firstValueFrom(this.http.get<any>(`${this.apiURL}/pendencia-cancelada?personId=${personId}`));
      return r?.data ?? [];
    } catch {
      return [];
    }
  }

  /** Null na falha (back antigo sem a rota, por exemplo): a lista monta só com as que têm cobrança. */
  async assinaturasCanceladas(personId: number): Promise<AssinaturaCancelada[] | null> {
    try {
      const r = await firstValueFrom(this.http.get<any>(`${this.apiURL}/assinaturas-canceladas?personId=${personId}`));
      return r?.data ?? [];
    } catch {
      return null;
    }
  }

  /** Falha na consulta devolve null: sem saber, a oferta não aparece (o CartãoSiM recusaria a venda). */
  async bloqueios(personId: number): Promise<BloqueioAdesao[] | null> {
    try {
      const r = await firstValueFrom(this.http.get<any>(`${this.apiURL}/bloqueios?personId=${personId}`));
      return r?.data ?? [];
    } catch (erro: any) {
      return erro?.status === 404 ? [] : null;
    }
  }

  async regularizar(personId: number, billIds: number[], vencimento: VencimentoRegularizacao, subscriptionId?: number): Promise<RegularizacaoGerada> {
    const assinatura = subscriptionId != null ? { subscriptionId } : {};
    const r = await this.enviar(this.http.post<any>(`${this.apiURL}/regularizar`, { personId, billIds, vencimento, ...assinatura }));
    const dado = r?.data?.[0];
    if (!dado?.masterBillId) throw new Error(this.mensagem(r));
    return dado;
  }

  async reverterRegularizacao(personId: number, masterBillId: number, subscriptionId?: number): Promise<PendenciaCartaoSim | null> {
    const assinatura = subscriptionId != null ? { subscriptionId } : {};
    const r = await this.enviar(this.http.post<any>(`${this.apiURL}/regularizar/${masterBillId}/reverter`, { personId, ...assinatura }));
    return r?.data?.[0] ?? null;
  }

  async linkFatura(masterBillId: number): Promise<PagamentoAdesao> {
    const r = await this.enviar(this.http.get<any>(`${this.apiURL}/regularizar/${masterBillId}/pagamento`));
    const dado = r?.data?.[0];
    if (!dado?.url) throw new Error(this.mensagem(r));
    return dado;
  }

  async cobrarFaturaNoTerminal(masterBillId: number, poiid: string, clinicId: number): Promise<void> {
    const r = await this.enviar(this.http.post<any>(`${this.apiURL}/regularizar/${masterBillId}/terminal`, { poiid, parcelas: 1, clinicId }));
    if (!r?.data?.[0]?.enviado) throw new Error(this.mensagem(r));
  }

  async enviarLinkFatura(masterBillId: number, clientId: number, url: string, clinicId: number, email: boolean, whatsApp: boolean): Promise<PagamentoAdesao> {
    const r = await this.enviar(this.http.post<any>(`${this.apiURL}/regularizar/${masterBillId}/link`, { clientId, url, clinicId, email, whatsApp }));
    const dado = r?.data?.[0];
    if (!dado?.enviado) throw new Error(this.mensagem(r));
    return dado;
  }

  async faturaPaga(masterBillId: number): Promise<boolean> {
    try {
      const r = await firstValueFrom(this.http.get<any>(`${this.apiURL}/regularizar/${masterBillId}/situacao`));
      return r?.data?.[0] === true;
    } catch {
      return false;
    }
  }

  /** Erro HTTP do back traz a mensagem útil no corpo; o Angular a esconderia. */
  private async enviar(pedido: Observable<any>): Promise<any> {
    try {
      return await firstValueFrom(pedido);
    } catch (erro: any) {
      throw new Error(this.mensagem(erro?.error));
    }
  }

  private mensagem(resposta: any): string {
    return resposta?.notifications?.[0]?.message
      ?? resposta?.feedback
      ?? 'Não foi possível concluir a adesão.';
  }
}
